import React from 'react';
import {
  EyeIcon,
  PencilIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

export default function HRHelp() {
  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-red-600 p-3 rounded-full">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">HR Disciplinary Management</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive guide to managing disciplinary cases, tracking investigation progress,
            and ensuring fair and thorough resolution of workplace incidents.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <DocumentTextIcon className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The HR Disciplinary Management system provides a structured workflow for handling
            disciplinary cases from initial complaint through investigation, deliberation, and resolution.
            Track progress through seven distinct stages with comprehensive documentation and stakeholder management.
          </p>
        </div>

        {/* Status Workflow */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Disciplinary Workflow Stages</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                <h3 className="font-semibold text-red-800">New Complaint</h3>
              </div>
              <p className="text-red-700 text-sm">Initial disciplinary case logged and assigned</p>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                <h3 className="font-semibold text-orange-800">Meeting with Accuser</h3>
              </div>
              <p className="text-orange-700 text-sm">Gather initial information from complainant</p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-yellow-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                <h3 className="font-semibold text-yellow-800">Witness Statements Requested</h3>
              </div>
              <p className="text-yellow-700 text-sm">Request statements from identified witnesses</p>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-purple-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                <h3 className="font-semibold text-purple-800">Witness Statements Received</h3>
              </div>
              <p className="text-purple-700 text-sm">All witness statements collected and reviewed</p>
            </div>

            <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-gray-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                <h3 className="font-semibold text-gray-800">Meeting with Employee</h3>
              </div>
              <p className="text-gray-700 text-sm">Employee provides their perspective and response</p>
            </div>

            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
              <div className="flex items-center mb-2">
                <span className="bg-indigo-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">6</span>
                <h3 className="font-semibold text-indigo-800">Deliberation</h3>
              </div>
              <p className="text-indigo-700 text-sm">HR and management review all evidence and decide on action</p>
            </div>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-lg md:col-span-2 lg:col-span-1">
              <div className="flex items-center mb-2">
                <span className="bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">7</span>
                <h3 className="font-semibold text-emerald-800">Complete</h3>
              </div>
              <p className="text-emerald-700 text-sm">Disciplinary action implemented and case closed</p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Case Management */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <EyeIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Case Management</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Status Tracking:</strong> Visual progress through 7-stage disciplinary process</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Case Navigation:</strong> Browse through multiple disciplinary cases</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-time Updates:</strong> Automatic refresh when case details change</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Case History:</strong> Complete audit trail of all changes and actions</span>
              </li>
            </ul>
          </div>

          {/* Investigation Tools */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Investigation Tools</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Witness Management:</strong> Track outstanding witness statements</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Statement Collection:</strong> Request and collect witness testimonies</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>AI Summary Import:</strong> Generate AI-powered summaries for employee perspectives</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Meeting Scheduling:</strong> Book disciplinary meetings and hearings</span>
              </li>
            </ul>
          </div>

          {/* Editable Fields */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <PencilIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Editable Documentation</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Employee Perspective:</strong> Document employee's response and explanation</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Agreement Status:</strong> Track if employee agrees with incident account</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Investigation Steps:</strong> Record all investigation activities undertaken</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Findings & Actions:</strong> Document conclusions and disciplinary measures</span>
              </li>
            </ul>
          </div>

          {/* Support & Follow-up */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Support & Follow-up</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Corrective Actions:</strong> Outline required improvements and changes</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Support Resources:</strong> Document support provided to employee</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Improvement Deadlines:</strong> Set and track improvement timeframes</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Progress Monitoring:</strong> Track employee progress and compliance</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Meeting Management */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CalendarDaysIcon className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Meeting Management</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📅 Meeting Types</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Initial Meetings:</strong> Gather information from complainant</li>
                <li><strong>Employee Hearings:</strong> Allow employee to respond to allegations</li>
                <li><strong>Witness Interviews:</strong> Collect statements from witnesses</li>
                <li><strong>Deliberation Sessions:</strong> Review evidence and decide on actions</li>
                <li><strong>Follow-up Meetings:</strong> Monitor progress and provide support</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⚙️ Meeting Features</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Automated Scheduling:</strong> Book meetings directly from case management</li>
                <li><strong>Calendar Integration:</strong> Sync with organizational calendar systems</li>
                <li><strong>Meeting Notes:</strong> Record discussions and decisions</li>
                <li><strong>Attendance Tracking:</strong> Document who participated in meetings</li>
                <li><strong>Action Items:</strong> Assign follow-up tasks and responsibilities</li>
              </ul>
            </div>
          </div>
        </div>

        {/* AI Integration */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <ChatBubbleLeftRightIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">AI-Powered Assistance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">🤖 AI Summary Generation</h3>
              <ul className="space-y-2 text-purple-100">
                <li>• Automatically generate summaries of employee perspectives</li>
                <li>• Extract key points from lengthy witness statements</li>
                <li>• Identify patterns and themes across multiple testimonies</li>
                <li>• Suggest appropriate disciplinary actions based on evidence</li>
                <li>• Generate comprehensive case summaries for management review</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">📊 Smart Recommendations</h3>
              <ul className="space-y-2 text-purple-100">
                <li>• Analyze similar past cases for consistency</li>
                <li>• Suggest investigation steps based on incident type</li>
                <li>• Recommend appropriate support resources</li>
                <li>• Flag potential legal or compliance issues</li>
                <li>• Provide templates for disciplinary letters and documentation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">HR Best Practices & Guidelines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⚖️ Fair Process</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Always give employee opportunity to respond</li>
                <li>• Maintain confidentiality throughout process</li>
                <li>• Document everything thoroughly</li>
                <li>• Apply policies consistently across cases</li>
                <li>• Allow employee to bring representation</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔍 Investigation</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Act promptly but don't rush investigations</li>
                <li>• Gather evidence from multiple sources</li>
                <li>• Interview witnesses separately when possible</li>
                <li>• Consider all perspectives objectively</li>
                <li>• Document all findings clearly</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use horizontal scrolling for status stepper</li>
                <li>• Tap status badges to see detailed information</li>
                <li>• Swipe between different disciplinary cases</li>
                <li>• Use voice-to-text for lengthy documentation</li>
                <li>• Save drafts regularly when working on mobile</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Documentation</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use clear, factual language in all records</li>
                <li>• Include dates, times, and participants</li>
                <li>• Record both verbal and written communications</li>
                <li>• Keep records organized and accessible</li>
                <li>• Maintain confidentiality of sensitive information</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Progressive Discipline</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Start with informal discussions for minor issues</li>
                <li>• Use verbal warnings before written warnings</li>
                <li>• Consider employee's overall performance history</li>
                <li>• Focus on improvement rather than punishment</li>
                <li>• Escalate only when necessary and appropriate</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🤝 Support Focus</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Provide resources for employee improvement</li>
                <li>• Set realistic and achievable goals</li>
                <li>• Offer training and development opportunities</li>
                <li>• Monitor progress and provide feedback</li>
                <li>• Recognize and reward positive changes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}